/**
 * Opt-in error reports (every build, behind `__ERROR_REPORTS__`; docs/plans/error-reports.md). On an error the user may open "Report this
 * error", read the whole report and send it from their own email app or copy it; the app sends nothing. The report is
 * built from three facts and nothing else: the error's type (only a name on the fixed list ERROR_CODES, never its
 * message, stack or properties), the app's version (the build ID) and the browser's name and major version. No screen
 * code (it tells the flow, e.g. foreign workers), no language, no step, no localised text: the body's labels are fixed
 * English, so it is the same whatever the UI language. Every value is checked against its pattern before it is written.
 */
import { ERROR_CODES, errorCode, type ErrorCode } from '../diagnostics/errorCodes';
import { recentErrors } from './recentErrors';
import { REPORT_EMAIL } from '../diagnostics/buildInfo';
import { browserOf } from './userAgent';

export interface ErrorReportFacts {
  errorType: ErrorCode;
  version: string;
  browser: string;
}

/** The subject: fixed, no detail of the error. */
export const ERROR_REPORT_SUBJECT = 'Magia Li — error report';

const VERSION = /^[0-9A-Za-z.+-]{1,40}$/;
const BROWSER = /^([A-Z][A-Za-z]+( [A-Z][A-Za-z]+)? \d{1,4}|unknown)$/;

/** The facts of one error: its listed type (or "other"), this build, this browser. Nothing else of `err` is read. */
export function errorReportFacts(err: unknown, version: string, userAgent: string): ErrorReportFacts {
  let errorType: ErrorCode = 'other';
  try {
    errorType = errorCode(err);
  } catch {
    // A hostile `name` getter: the type stays "other".
  }
  return { errorType, version, browser: browserOf(userAgent) };
}

/** Throws on any value outside its pattern: the body can only ever hold the three whitelisted facts. */
export function assertErrorReportWhitelisted(f: ErrorReportFacts): void {
  if (!(ERROR_CODES as readonly string[]).includes(f.errorType)) throw new Error('error report: the error type is not whitelisted');
  if (!VERSION.test(f.version)) throw new Error('error report: the version is not whitelisted');
  if (!BROWSER.test(f.browser)) throw new Error('error report: the browser is not whitelisted');
  if (Object.keys(f).length !== 3) throw new Error('error report: unexpected fields');
}

/** The whole report, exactly as shown to the user and as the email body. */
export function errorReportBody(f: ErrorReportFacts): string {
  assertErrorReportWhitelisted(f);
  return ['Magia Li error report', `Error type: ${f.errorType}`, `App version: ${f.version}`, `Browser: ${f.browser}`].join('\n');
}

/** A mailto: the user's own email app opens; nothing is sent until the user sends it there. */
export function errorReportHref(f: ErrorReportFacts): string {
  return `mailto:${REPORT_EMAIL}?subject=${encodeURIComponent(ERROR_REPORT_SUBJECT)}&body=${encodeURIComponent(errorReportBody(f))}`;
}

/** Everything "Report this error" shows and links to (src/beta/errorReportOffer.ts draws it; nothing else goes on the screen). */
export interface ErrorReportView {
  summary: string;
  intro: string;
  label: string;
  /** The read-only box: exactly the email body. */
  text: string;
  href: string;
  email: string;
  emailHelp: string;
  copy: string;
  copied: string;
}

/**
 * The offer's texts and report. `err` when the error state has it; without it (a screen drawn after the error was
 * caught), the latest noted error's listed type (src/beta/recentErrors.ts), else "other".
 */
export function errorReportView(tr: (key: string) => string, version: string, userAgent: string, err?: unknown): ErrorReportView {
  const facts = err === undefined ? { ...errorReportFacts(undefined, version, userAgent), errorType: recentErrors().at(-1)?.code ?? 'other' } : errorReportFacts(err, version, userAgent);
  return {
    summary: tr('betaAids.errorReport.summary'),
    intro: tr('betaAids.errorReport.intro'),
    label: tr('betaAids.errorReport.label'),
    text: errorReportBody(facts),
    href: errorReportHref(facts),
    email: tr('betaAids.errorReport.email'),
    emailHelp: tr('betaAids.errorReport.emailHelp'),
    copy: tr('diag.copy'),
    copied: tr('diag.copied'),
  };
}
